import { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlacesAutocomplete({ value, onChange, onSelect, placeholder, testId, autoFocus }) {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [configured, setConfigured] = useState(null);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (input) => {
    if (input.length < 2) { setSuggestions([]); return; }
    try {
      const resp = await fetch(`${API}/api/places/autocomplete?input=${encodeURIComponent(input)}&types=establishment`);
      const data = await resp.json();
      setConfigured(data.configured);
      if (data.predictions?.length) {
        setSuggestions(data.predictions);
        setIsOpen(true);
      } else {
        setSuggestions([]);
        setIsOpen(false);
      }
    } catch {
      setSuggestions([]);
    }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = async (prediction) => {
    onChange(prediction.main_text || prediction.description);
    setIsOpen(false);
    setSuggestions([]);
    if (prediction.place_id && onSelect) {
      try {
        const resp = await fetch(`${API}/api/places/details?place_id=${prediction.place_id}`);
        const data = await resp.json();
        if (data.result) onSelect(data.result);
      } catch { /* graceful fallback */ }
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => suggestions.length && setIsOpen(true)}
          placeholder={placeholder}
          className="w-full px-5 py-4 text-base outline-none transition-all pr-10"
          style={{ backgroundColor: '#FDFBF7', border: '1px solid #D4C9B8', borderRadius: 0, color: '#3C3226' }}
          onMouseEnter={e => e.target.style.borderColor = '#B8985A'}
          onMouseLeave={e => { if (document.activeElement !== e.target) e.target.style.borderColor = '#D4C9B8'; }}
          data-testid={testId}
          autoFocus={autoFocus}
          autoComplete="off"
        />
        {configured && (
          <Search size={14} className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#8C7E6E' }} />
        )}
      </div>

      <AnimatePresence>
        {isOpen && suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="absolute z-50 left-0 right-0 mt-1 shadow-lg overflow-hidden"
            style={{ backgroundColor: '#FDFBF7', border: '1px solid #D4C9B8' }}
            data-testid="places-suggestions"
          >
            {suggestions.map((s, i) => (
              <button
                key={s.place_id || i}
                onClick={() => handleSelect(s)}
                className="w-full text-left px-5 py-3 transition-colors hover:bg-[#F0EBE3] flex flex-col"
                data-testid={`places-suggestion-${i}`}
              >
                <span className="text-sm font-medium" style={{ color: '#3C3226' }}>{s.main_text}</span>
                {s.secondary_text && (
                  <span className="text-xs mt-0.5" style={{ color: '#8C7E6E' }}>{s.secondary_text}</span>
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
